import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, TrendingUp, TrendingDown, Package, Tag, LogOut, Menu, X, Tags, Store, Sun, Moon, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: TrendingUp, label: 'Entradas', path: '/transactions?type=income' },
    { icon: TrendingDown, label: 'Saídas', path: '/transactions?type=expense' },
    { icon: Clock, label: 'Prazo Fornecedores', path: '/supplier-deadlines' },
    { icon: Package, label: 'Produtos', path: '/products' },
    { icon: Store, label: 'Marketplaces', path: '/marketplaces' },
    { icon: Tags, label: 'Categorias de Produtos', path: '/product-categories' },
    { icon: Tag, label: 'Categorias de Transações', path: '/categories' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    const pathWithoutQuery = path.split('?')[0];
    if (pathWithoutQuery === '/categories') return location.pathname === '/categories';
    if (pathWithoutQuery === '/product-categories') return location.pathname === '/product-categories';
    if (pathWithoutQuery === '/supplier-deadlines') return location.pathname === '/supplier-deadlines';
    if (pathWithoutQuery === '/marketplaces') return location.pathname === '/marketplaces';
    return location.pathname.includes(pathWithoutQuery);
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout">
      {/* Sidebar Desktop - Fixed */}
      <aside className="hidden md:flex w-64 fixed top-0 left-0 h-screen bg-card border-r border-border flex-col z-40">
        <div className="p-4 border-b border-border flex items-center justify-center">
          <img
            src="/logo.png"
            alt="IC Store"
            className="h-16 w-auto object-contain rounded-lg p-1"
            data-testid="app-logo"
          />
        </div>

        <nav className="flex-1 p-3 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-left text-sm transition-colors ${
                  active
                    ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400 font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-border">
          <button
            onClick={() => setDarkMode(!darkMode)}
            data-testid="dark-mode-toggle"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span>{darkMode ? 'Modo Claro' : 'Modo Escuro'}</span>
          </button>
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-indigo-600/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-semibold text-sm">
              {user.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
          >
            <LogOut size={18} className="mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-card border-b border-border z-50">
        <div className="flex items-center justify-between p-3">
          <img
            src="/logo.png"
            alt="IC Store"
            className="h-11 w-auto object-contain rounded p-0.5"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 hover:bg-accent rounded-lg text-muted-foreground"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              className="p-2 hover:bg-accent rounded-lg text-foreground"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-card border-b border-border shadow-lg">
            <nav className="p-4">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 text-left transition-colors ${
                      active
                        ? 'bg-indigo-600/10 text-indigo-600 dark:text-indigo-400 font-medium'
                        : 'text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
              >
                <LogOut size={20} />
                <span>Sair</span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content - offset for fixed sidebar */}
      <main className="flex-1 md:ml-64 overflow-y-auto md:pt-0 pt-14 min-h-screen">
        <div className="max-w-7xl mx-auto w-full p-4 sm:p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
